import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import './TreeChart.css';
import { fetchCompanyChildren, fetchClanMemberById } from '../services/api';

/* global wx */

const TreeChart = ({ data }) => {
    const svgRef = useRef();
    const containerRef = useRef();
    const zoomBehaviorRef = useRef(null); // 保存 zoom 行为实例
    const [collapsedNodes, setCollapsedNodes] = useState(new Set());
    const currentTransformRef = useRef(null);
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
        hasChanged: false
    });
    const [loadingNodes, setLoadingNodes] = useState(new Set()); // 跟踪正在加载的节点
    const [treeData, setTreeData] = useState(data); // 存储完整的树数据
    const [detailModalVisible, setDetailModalVisible] = useState(false); // 详情弹窗可见性
    const [detailData, setDetailData] = useState(null); // 详情数据
    const [detailLoading, setDetailLoading] = useState(false); // 详情加载状态
    const nodeDetailsCache = useRef(new Map()); // 节点详情缓存
    const [mapPickerVisible, setMapPickerVisible] = useState(false); // 地图选择弹窗可见性
    const [mapPickerData, setMapPickerData] = useState(null); // 地图选择数据 { lat, lng, name }

    // Define colors
    const blueColor = "#6495ED"; // Blue color for male borders and lines
    const femaleBorderColor = "#ff69b4"; // Pink color for female border
    const femaleFillColor = "#fff0f6"; // Light pink fill for female nodes

    // 创建用于渲染的数据预处理函数
    const prepareTreeData = useCallback((rawData) => {
        // 递归处理每个节点
        const processNode = (node) => {
            // 确保每个节点都有hasChildren属性
            if (node.hasChildren === undefined) {
                // 如果未定义，则根据children数组判断
                node.hasChildren = Array.isArray(node.children) && node.children.length > 0;
            }

            // 处理子节点
            if (Array.isArray(node.children)) {
                node.children = node.children.map(child => processNode(child));
            } else {
                node.children = [];
            }

            return node;
        };

        // 处理整个树
        return processNode(JSON.parse(JSON.stringify(rawData)));
    }, []);

    // 更新树数据的useEffect
    useEffect(() => {
        if (data) {
            // 预处理数据，确保每个节点都有hasChildren属性
            const processedData = prepareTreeData(data);
            setTreeData(processedData);
        }
    }, [data, prepareTreeData]);

    // 更新树数据的函数
    const updateTreeData = useCallback((originalData, nodeId, children) => {
        // 递归函数，查找并更新指定ID的节点
        const updateNode = (node) => {
            if (node.id === nodeId) {
                // 找到节点，更新其子节点
                return { ...node, children };
            } else if (node.children) {
                // 递归处理子节点
                return {
                    ...node,
                    children: node.children.map(child => updateNode(child))
                };
            }
            // 未找到，返回原节点
            return node;
        };

        // 更新整个树
        return updateNode(originalData);
    }, []);

    // 异步加载子节点
    const loadChildrenAsync = useCallback(async (nodeId) => {
        if (loadingNodes.has(nodeId)) {
            return; // 避免重复加载
        }

        try {
            // 设置节点为加载状态
            setLoadingNodes(prev => new Set([...prev, nodeId]));

            // 调用API获取子节点
            const response = await fetchCompanyChildren(nodeId);

            if (response.success && response.data) {
                // 更新树数据
                setTreeData(prevData => updateTreeData(prevData, nodeId, response.data));

                // 加载完成后展开节点，这样直接显示子节点
                setCollapsedNodes(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(nodeId); // 从折叠集合中移除，实现展开效果
                    return newSet;
                });
            }
        } catch (error) {
            console.error('加载子节点失败:', error);
        } finally {
            // 清除加载状态
            setLoadingNodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(nodeId);
                return newSet;
            });
        }
    }, [loadingNodes, updateTreeData]);

    // 获取节点详情
    const fetchNodeDetail = useCallback(async (nodeId) => {
        // 先检查缓存
        if (nodeDetailsCache.current.has(nodeId)) {
            setDetailData(nodeDetailsCache.current.get(nodeId));
            setDetailModalVisible(true);
            return;
        }

        try {
            setDetailLoading(true);
            setDetailModalVisible(true);
            setDetailData(null);

            const response = await fetchClanMemberById(nodeId);

            if (response.success && response.data) {
                // 缓存结果
                nodeDetailsCache.current.set(nodeId, response.data);
                setDetailData(response.data);
            } else {
                setDetailData({ error: response.error || '获取详情失败' });
            }
        } catch (error) {
            console.error('获取节点详情失败:', error);
            setDetailData({ error: '获取详情失败' });
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // 关闭详情弹窗
    const closeDetailModal = useCallback(() => {
        setDetailModalVisible(false);
        setDetailData(null);
        setDetailLoading(false);
    }, []);

    // Function to toggle node collapse state
    const toggleNode = useCallback((d) => {
        // Store the current transform before toggling
        if (currentTransformRef.current) {
            const nodeId = d.data.id;

            const hasChildren = Array.isArray(d.data.children) && d.data.children.length > 0;

            // 如果没有子节点但hasChildren为true，触发异步加载
            if (!hasChildren && d.data.hasChildren === true) {
                loadChildrenAsync(nodeId);
                // 不需要在这里更改折叠状态，loadChildrenAsync函数会在加载完成后处理
            } else {
                // 有子节点，切换折叠状态
                setCollapsedNodes(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(nodeId)) {
                        newSet.delete(nodeId);
                    } else {
                        newSet.add(nodeId);
                    }
                    return newSet;
                });
            }
        }
    }, [loadChildrenAsync]);

    // Function to check if a node should be visible
    const isVisible = useCallback((d) => {
        // Root is always visible
        if (d.depth === 0) return true;

        // If any ancestor is collapsed, this node is not visible
        let ancestor = d.parent;
        while (ancestor) {
            if (collapsedNodes.has(ancestor.data.id)) {
                return false;
            }
            ancestor = ancestor.parent;
        }

        return true;
    }, [collapsedNodes]);

    // Function to get tree dimensions
    const getTreeDimensions = useCallback((nodes) => {
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;

        nodes.forEach(d => {
            minX = Math.min(minX, d.x - 60); // Account for node width (120/2)
            maxX = Math.max(maxX, d.x + 60); // Account for node width (120/2)
            minY = Math.min(minY, d.y - 30); // Account for node height (60/2)
            maxY = Math.max(maxY, d.y + 30); // Account for node height (60/2)
        });

        return {
            minX, maxX, minY, maxY,
            width: maxX - minX,
            height: maxY - minY
        };
    }, []);

    // Function to calculate optimal transform
    const calculateOptimalTransform = useCallback((treeDimensions, containerWidth, containerHeight) => {
        const { minX, minY, width: treeWidth, height: treeHeight } = treeDimensions;

        // Calculate padding (as a percentage of container size)
        const paddingX = containerWidth * 0.05; // 减小padding，让树图更大
        const paddingY = containerHeight * 0.05;

        // Calculate the scale to fit the tree in the viewport with padding
        const scaleX = (containerWidth - paddingX * 2) / treeWidth;
        const scaleY = (containerHeight - paddingY * 2) / treeHeight;
        
        // 移除最大值限制1，允许放大以填充屏幕
        // 设置最小缩放比例，确保手机上也能看清
        const minScale = Math.min(containerWidth, containerHeight) < 500 ? 1.2 : 0.8; // 小屏幕最小1.2倍
        let scale = Math.min(scaleX, scaleY) * 0.95; // 稍微缩小5%确保边缘可见
        
        // 确保不低于最小缩放
        scale = Math.max(scale, minScale);

        // Calculate offsets to center the tree
        const offsetX = (containerWidth / scale - treeWidth) / 2 - minX;
        const offsetY = (containerHeight / scale - treeHeight) / 2 - minY;

        // Create the transform
        return d3.zoomIdentity
            .scale(scale)
            .translate(offsetX, offsetY);
    }, []);

    // Effect for window resize
    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
                hasChanged: true
            });
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Main rendering effect
    useEffect(() => {
        if (!treeData || !svgRef.current) return;

        // Store the current transform for later use
        const previousTransform = currentTransformRef.current;

        // Clear any existing SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Get the actual dimensions of the container
        const containerWidth = dimensions.width;
        const containerHeight = dimensions.height;

        // Create the SVG container with full width and height
        const svg = d3.select(svgRef.current)
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", [0, 0, containerWidth, containerHeight].join(' '));

        // Define arrowhead marker
        svg.append("defs").append("marker")
            .attr("id", "arrowhead")
            .attr("viewBox", "0 -5 10 10")
            .attr("refX", 10)
            .attr("refY", 0)
            .attr("orient", "auto")
            .attr("markerWidth", 6)
            .attr("markerHeight", 6)
            .attr("xoverflow", "visible")
            .append("path")
            .attr("d", "M0,-5L10,0L0,5")
            .attr("fill", blueColor) // Blue arrow
            .style("stroke", "none");

        // Create a group for the tree with initial transform
        const g = svg.append("g");

        // Create the hierarchical data structure
        const root = d3.hierarchy(treeData);

        // Define tree layout - vertical orientation
        const treeLayout = d3.tree()
            .nodeSize([150, 140]); // Horizontal and vertical spacing

        // Assign the data to the tree layout
        treeLayout(root);

        // Filter visible nodes and links
        const visibleNodes = root.descendants().filter(isVisible);
        const visibleLinks = root.links().filter(d => isVisible(d.source) && isVisible(d.target));

        // Calculate dimensions for centering
        const treeDimensions = getTreeDimensions(visibleNodes);

        // Add links between nodes
        const links = g.selectAll(".link")
            .data(visibleLinks)
            .enter()
            .append("g")
            .attr("class", "link");

        // Create paths for links with segments
        links.each(function (d) {
            const link = d3.select(this);
            const sourceX = d.source.x;
            const sourceY = d.source.y;
            const targetX = d.target.x;
            const targetY = d.target.y;
            const midY = (sourceY + targetY) / 2;

            // Vertical line from source
            link.append("path")
                .attr("fill", "none")
                .attr("stroke", blueColor) // Blue line
                .attr("stroke-width", 1)
                .attr("d", `M${sourceX},${sourceY} L${sourceX},${midY}`);

            // Horizontal line
            link.append("path")
                .attr("fill", "none")
                .attr("stroke", blueColor) // Blue line
                .attr("stroke-width", 1)
                .attr("d", `M${sourceX},${midY} L${targetX},${midY}`);

            // Vertical line to target with arrow
            link.append("path")
                .attr("fill", "none")
                .attr("stroke", blueColor) // Blue line
                .attr("stroke-width", 1)
                .attr("marker-end", "url(#arrowhead)")
                .attr("d", `M${targetX},${midY} L${targetX},${targetY - 30}`); // Stop before the node for arrow

            // Add percentage label to the side of the vertical line with arrow if available
            if (d.target.data.percentage) {
                // Position the label to the right of the vertical line
                const labelX = targetX + 15; // 15px to the right of the line

                // Add percentage text with white background
                link.append("text")
                    .attr("x", labelX)
                    .attr("y", (midY + targetY - 30) / 2)
                    .attr("text-anchor", "start")
                    .attr("dominant-baseline", "central")
                    .attr("font-size", 11)
                    .attr("font-weight", "bold")
                    .attr("fill", "#000") // Pure black text
                    .attr("stroke", "white") // White outline for better visibility
                    .attr("stroke-width", 0.5)
                    .attr("paint-order", "stroke") // Draw stroke behind text
                    .text(`${d.target.data.percentage}%`);
            }
        });

        // Create a group for each node
        const node = g.selectAll(".node")
            .data(visibleNodes)
            .enter()
            .append("g")
            .attr("class", d => "node" + (d.children ? " node--internal" : " node--leaf"))
            .attr("transform", d => `translate(${d.x},${d.y})`)
            .style("cursor", d => d.data.children && d.data.children.length > 0 ? "pointer" : "default"); // Change cursor for parent nodes

        // Add rectangles for each node
        node.append("rect")
            .attr("width", 120)
            .attr("height", 60) // Increased height for better text spacing
            .attr("x", -60)
            .attr("y", -30)
            .attr("rx", 4)
            .attr("ry", 4)
            .attr("fill", d => d.data.sex === '2' ? femaleFillColor : "#fff") // 女性用粉色填充
            .attr("stroke", d => d.data.sex === '2' ? femaleBorderColor : blueColor) // 女性用粉色边框
            .attr("stroke-width", 1);

        // Add labels for each node with text wrapping
        node.append("text")
            .attr("text-anchor", "middle")
            .attr("font-size", 12)
            .attr("dy", "-0.3em") // Start a bit higher to accommodate multiple lines
            .attr("fill", d => d.data.sex === '2' ? "#c2185b" : "#333") // 女性用深粉色
            .text(d => d.data.name)
            .call(wrapText, 110); // Apply text wrapping

        // 添加详情图标（问号图标）
        node.each(function (d) {
            const nodeGroup = d3.select(this);
            const nodeId = d.data.id;
            const isFemale = d.data.sex === '2';

            // 添加详情图标背景圆
            nodeGroup.append("circle")
                .attr("cx", 55)
                .attr("cy", -30)
                .attr("r", 10)
                .attr("fill", "white")
                .attr("stroke", isFemale ? femaleBorderColor : "#1890ff")
                .attr("stroke-width", 1.5)
                .attr("cursor", "pointer")
                .attr("class", "detail-icon")
                .on("click", (event) => {
                    event.stopPropagation();
                    fetchNodeDetail(nodeId);
                });

            // 添加问号文字
            nodeGroup.append("text")
                .attr("x", 55)
                .attr("y", -30)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size", 12)
                .attr("font-weight", "bold")
                .attr("fill", isFemale ? femaleBorderColor : "#1890ff")
                .attr("cursor", "pointer")
                .attr("class", "detail-icon")
                .text("?")
                .on("click", (event) => {
                    event.stopPropagation();
                    fetchNodeDetail(nodeId);
                });
        });

        // 移除加载节点的旋转效果，使用静态图标
        node.filter(d => loadingNodes.has(d.data.id)).each(function (d) {
            const nodeGroup = d3.select(this);

            // 添加加载指示器背景，不使用旋转效果
            nodeGroup.append("circle")
                .attr("cx", 0)
                .attr("cy", 30) // Position at the bottom of the node
                .attr("r", 10)
                .attr("fill", "#f5f5f5")
                .attr("stroke", blueColor)
                .attr("stroke-width", 1);

            // 添加加载文本
            nodeGroup.append("text")
                .attr("x", 0)
                .attr("y", 30)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size", 10)
                .attr("fill", blueColor)
                .attr("font-weight", "bold")
                .text("加载中");
        });

        // 添加展开/折叠图标
        node.filter(d => {
            // 节点标记为hasChildren=true，并且不在加载状态
            return d.data.hasChildren === true &&
                !loadingNodes.has(d.data.id);
        }).each(function (d) {
            const nodeGroup = d3.select(this);
            const nodeId = d.data.id;
            const hasChildren = Array.isArray(d.data.children) && d.data.children.length > 0;
            const isCollapsed = collapsedNodes.has(nodeId);
            const isFemale = d.data.sex === '2';

            // 确定显示的符号：
            // 如果有子节点且未折叠，显示减号(-)
            // 其他情况显示加号(+)
            const showMinusSign = hasChildren && !isCollapsed;

            // 添加展开/折叠图标背景
            nodeGroup.append("circle")
                .attr("cx", 0)
                .attr("cy", 30) // Position at the bottom of the node
                .attr("r", 8)
                .attr("fill", "white")
                .attr("stroke", isFemale ? femaleBorderColor : blueColor)
                .attr("stroke-width", 1)
                .attr("cursor", "pointer")
                .on("click", (event) => {
                    event.stopPropagation();
                    // 如果没有子节点但hasChildren为true，触发异步加载
                    if (!hasChildren && d.data.hasChildren === true) {
                        loadChildrenAsync(nodeId);
                        // 不需要在这里更改折叠状态，loadChildrenAsync函数会在加载完成后处理
                    } else {
                        // 有子节点，切换折叠状态
                        setCollapsedNodes(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(nodeId)) {
                                newSet.delete(nodeId);
                            } else {
                                newSet.add(nodeId);
                            }
                            return newSet;
                        });
                    }
                });

            // 添加加减号
            nodeGroup.append("text")
                .attr("x", 0)
                .attr("y", 30)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-size", 12)
                .attr("font-weight", "bold")
                .attr("fill", isFemale ? femaleBorderColor : blueColor)
                .attr("cursor", "pointer")
                .text(showMinusSign ? "−" : "+") // 根据状态决定显示+或-
                .on("click", (event) => {
                    event.stopPropagation();
                    // 如果没有子节点但hasChildren为true，触发异步加载
                    if (!hasChildren && d.data.hasChildren === true) {
                        loadChildrenAsync(nodeId);
                        // 不需要在这里更改折叠状态，loadChildrenAsync函数会在加载完成后处理
                    } else {
                        // 有子节点，切换折叠状态
                        setCollapsedNodes(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(nodeId)) {
                                newSet.delete(nodeId);
                            } else {
                                newSet.add(nodeId);
                            }
                            return newSet;
                        });
                    }
                });
        });

        // Add zoom behavior
        const zoom = d3.zoom()
            .scaleExtent([0.2, 3]) // Allow more zoom range
            .on("zoom", (event) => {
                g.attr("transform", event.transform);
                // Store the current transform for later use
                currentTransformRef.current = event.transform;
            });

        // 保存 zoom 行为实例供外部使用
        zoomBehaviorRef.current = zoom;

        svg.call(zoom);

        // Set initial transform - either restore previous or calculate new one
        let initialTransform;

        if (previousTransform && !dimensions.hasChanged) {
            // Use the previous transform if available and dimensions haven't changed
            initialTransform = previousTransform;
        } else {
            // Calculate new optimal transform
            initialTransform = calculateOptimalTransform(treeDimensions, containerWidth, containerHeight);

            // Reset the hasChanged flag after applying the new transform
            if (dimensions.hasChanged) {
                setDimensions(prev => ({ ...prev, hasChanged: false }));
            }
        }

        // Apply the transform
        svg.call(zoom.transform, initialTransform);

    }, [treeData, dimensions, collapsedNodes, isVisible, toggleNode, getTreeDimensions, calculateOptimalTransform, loadingNodes, prepareTreeData, blueColor, fetchNodeDetail, loadChildrenAsync]);

    // Function to wrap text with truncation
    const wrapText = (text, width) => {
        text.each(function () {
            const text = d3.select(this);
            const words = text.text().split('').reverse();
            const lineHeight = 1.4; // Increased line height for better spacing
            const y = text.attr("y");
            const dy = parseFloat(text.attr("dy"));

            let line = [];
            let lineNumber = 0;
            let tspan = text.text(null)
                .append("tspan")
                .attr("x", 0)
                .attr("y", y)
                .attr("dy", dy + "em");

            let word;
            let lineLength = 0;
            const maxCharsPerLine = Math.floor(width / 12); // Approximate chars per line
            const maxLines = 2; // Maximum number of lines before truncating

            while (words.length && lineNumber < maxLines) {
                word = words.pop();
                line.push(word);
                lineLength++;

                if (lineLength >= maxCharsPerLine) {
                    // If this is the last allowed line and there are more words
                    if (lineNumber === maxLines - 1 && words.length > 0) {
                        // Add ellipsis to indicate truncation
                        tspan.text(line.join("").slice(0, -2) + "...");
                        break;
                    } else {
                        tspan.text(line.join(""));
                        line = [];
                        lineLength = 0;
                        lineNumber++;

                        // Only create a new tspan if we're not at max lines
                        if (lineNumber < maxLines) {
                            tspan = text.append("tspan")
                                .attr("x", 0)
                                .attr("y", y)
                                .attr("dy", lineNumber * lineHeight + dy + "em");
                        }
                    }
                }
            }

            // Handle remaining text
            if (line.length > 0 && lineNumber < maxLines) {
                // If this is the last allowed line and there are more words
                if (lineNumber === maxLines - 1 && words.length > 0) {
                    tspan.text(line.join("").slice(0, -2) + "...");
                } else {
                    tspan.text(line.join(""));
                }
            }
        });
    };

    // 添加节点加载指示器
    const renderLoadingIndicator = useCallback(() => {
        if (loadingNodes.size === 0) return null;

        return (
            <div className="loading-indicator">
                <div className="loading-status">
                    <span>正在加载 {loadingNodes.size} 个节点</span>
                </div>
            </div>
        );
    }, [loadingNodes.size]);

    // 缩放控制函数
    const handleZoomIn = useCallback(() => {
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        const svg = d3.select(svgRef.current);
        const currentTransform = d3.zoomTransform(svgRef.current);
        const newTransform = currentTransform.scale(1.3);
        svg.transition().duration(300).call(zoomBehaviorRef.current.transform, newTransform);
    }, []);

    const handleZoomOut = useCallback(() => {
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        const svg = d3.select(svgRef.current);
        const currentTransform = d3.zoomTransform(svgRef.current);
        const newTransform = currentTransform.scale(0.7);
        svg.transition().duration(300).call(zoomBehaviorRef.current.transform, newTransform);
    }, []);

    const handleZoomReset = useCallback(() => {
        if (!svgRef.current || !treeData || !zoomBehaviorRef.current) return;
        const svg = d3.select(svgRef.current);
        const root = d3.hierarchy(treeData);
        const treeLayout = d3.tree().nodeSize([150, 140]);
        treeLayout(root);
        const visibleNodes = root.descendants().filter(isVisible);
        const treeDimensions = getTreeDimensions(visibleNodes);
        const resetTransform = calculateOptimalTransform(treeDimensions, dimensions.width, dimensions.height);
        svg.transition().duration(500).call(zoomBehaviorRef.current.transform, resetTransform);
    }, [treeData, isVisible, getTreeDimensions, calculateOptimalTransform, dimensions]);

    // 检测是否为移动设备
    const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) < 500;

    // 平台检测
    const isWechat = typeof navigator !== 'undefined' && /micromessenger/i.test(navigator.userAgent);
    const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

    // 地图APP配置
    const mapApps = [
        {
            name: '高德地图',
            icon: '🗺️',
            getUrl: (lat, lng, name) => `https://uri.amap.com/marker?position=${lng},${lat}&name=${encodeURIComponent(name)}`,
        },
        {
            name: '百度地图',
            icon: '📍',
            getUrl: (lat, lng, name) => `https://api.map.baidu.com/marker?location=${lat},${lng}&title=${encodeURIComponent(name)}&output=html`,
        },
        {
            name: '腾讯地图',
            icon: '🧭',
            getUrl: (lat, lng, name) => `https://apis.map.qq.com/uri/v1/marker?marker=coord:${lat},${lng};title:${encodeURIComponent(name)}`,
        },
        ...(isIOS ? [{
            name: '苹果地图',
            icon: '🍎',
            getUrl: (lat, lng, name) => `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(name)}`,
        }] : []),
    ];

    // 打开地图选择弹窗
    const openMapPicker = useCallback((lat, lng, name) => {
        setMapPickerData({ lat, lng, name: name || '目的地' });
        setMapPickerVisible(true);
    }, []);

    // 关闭地图选择弹窗
    const closeMapPicker = useCallback(() => {
        setMapPickerVisible(false);
        setMapPickerData(null);
    }, []);

    // 执行导航
    const handleNavigate = useCallback((app, data) => {
        const { lat, lng, name } = data;
        const url = app.getUrl(lat, lng, name);
        // 使用 location.href 跳转，H5和微信浏览器均可正常打开
        // 系统/浏览器会自动识别并提示是否用对应APP打开
        window.location.href = url;
        // 延迟关闭弹窗
        setTimeout(() => {
            setMapPickerVisible(false);
            setMapPickerData(null);
        }, 300);
    }, []);
    
    // 统一的导航处理函数
    const handleLocationNavigation = useCallback((lat, lng, name, address = '') => {
        if (!lat || !lng) {
            alert('坐标信息缺失');
            return;
        }

        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        
        if (isNaN(latNum) || isNaN(lngNum)) {
            alert('坐标格式错误');
            return;
        }

        const locName = name || '目的地';
        const locAddress = address || locName;

        // 方案1: 微信小程序 web-view 环境 — 跳转到小程序中转页面
        // 通过 wx.miniProgram.navigateTo 跳转，由中转页面调用 uni.openLocation
        if (isWechat && typeof wx !== 'undefined' && wx.miniProgram) {
            console.log('检测到小程序 web-view 环境，跳转到中转页面');
            try {
                wx.miniProgram.navigateTo({
                    url: `/package-clan/pages/clan-web/open-location?latitude=${latNum}&longitude=${lngNum}&name=${encodeURIComponent(locName)}&address=${encodeURIComponent(locAddress)}`
                });
                return; // 跳转成功，不需要继续执行
            } catch (error) {
                console.error('navigateTo 中转页面失败，降级到地图选择弹窗:', error);
                // 继续执行下面的降级方案
            }
        }
        
        // 方案2: 普通H5环境（浏览器、微信浏览器等） — 显示地图选择弹窗
        openMapPicker(latNum, lngNum, locName);
    }, [isWechat,openMapPicker]);
	// 纯个位数映射
	const numberToChineseSimple = (num) => {
		//
	  const chineseNumbers = ['大','大', '二', '三', '四', '五', '六', '七', '八', '九'];
	  //排序为0或1的，都属于老大
	  const index = Number(num);
	  if (isNaN(index) || index < 0 || index > 9) return String(num); // 超出范围返回原值
	  return chineseNumbers[index];
	};
	const memberTypeText=(memberType)=> {
      const types = { '0': '亲生', '1': '收养', '2': '继亲','3':'妻子' };
      return types[memberType] || '未知';
    };
    return (
        <div ref={containerRef} className="tree-chart-container" style={{ width: '100%', flex: 1, position: 'relative', overflow: 'hidden' }}>
            <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
            {renderLoadingIndicator()}

            {/* 缩放控制按钮 */}
            <div className="zoom-controls">
                <button className="zoom-btn" onClick={handleZoomIn} title="放大">+</button>
                <button className="zoom-btn" onClick={handleZoomReset} title="重置">⟲</button>
                <button className="zoom-btn" onClick={handleZoomOut} title="缩小">−</button>
            </div>

            {/* 移动端操作提示 */}
            {isMobile && (
                <div className="mobile-hint">
                    <span>💡 双指捏合可缩放</span>
                </div>
            )}

            {/* 详情弹窗 */}
            {detailModalVisible && (
                <div className="detail-modal-overlay" onClick={closeDetailModal}>
                    <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="detail-modal-header">
                            <span className="detail-modal-title">节点详情</span>
                            <span className="detail-modal-close" onClick={closeDetailModal}>×</span>
                        </div>
                        <div className="detail-modal-body">
                            {detailLoading ? (
                                <div className="detail-loading">加载中...</div>
                            ) : detailData ? (
                                detailData.error ? (
                                    <div className="detail-error">{detailData.error}</div>
                                ) : (
                                    <div className="detail-content">
										{/* 
                                        {Object.entries(detailData).map(([key, value]) => (
                                            <div key={key} className="detail-row">
                                                <span className="detail-label">{key}：</span>
                                                <span className="detail-value">{String(value)}</span>
                                            </div>
                                        ))}
										*/}
										<div key="code" className="detail-row">
										    <span className="detail-label">编码：</span>
										    <span className="detail-value">{String(detailData.code)}</span>
										</div>
										<div key="realName" className="detail-row">
										    <span className="detail-label">姓名(户口本名字)：</span>
										    <span className="detail-value">{String(detailData.realName??'暂无')}</span>
										</div>
										<div key="aliasName" className="detail-row">
										    <span className="detail-label">族谱名：</span>
										    <span className="detail-value">{String(detailData.aliasName??'暂无')}</span>
										</div>
										<div key="nickName" className="detail-row">
										    <span className="detail-label">昵称：</span>
										    <span className="detail-value">{String(detailData.nickName??'暂无')}</span>
										</div>
										<div key="sex" className="detail-row">
										    <span className="detail-label">性别：</span>
										    <span className="detail-value">{detailData.sex==='1'?'男':'女'}</span>
										</div>										
										<div key="birthDate" className="detail-row">
										    <span className="detail-label">出生日期：</span>
										    <span className="detail-value">{String(detailData.birthDate??'暂无')}</span>
										</div>
										<div key="passAwayStatus" className="detail-row">
										    <span className="detail-label">是否逝世：</span>
										    <span className="detail-value">{detailData.passAwayStatus==='1'?'是':'否'}</span>
										</div>
										{
										detailData.passAwayStatus==='1'?(
										<div key="passAwayDate" className="detail-row">
										    <span className="detail-label">逝世日期：</span>
										    <span className="detail-value">{String(detailData.passAwayDate??'暂无')}</span>
										</div>
										):''
										}
										
										{
										detailData.passAwayStatus==='1'?
									(
									<div key="cemeteryLocationDesc" className="detail-row">
									    <span className="detail-label">墓地位置：</span>
									    <span className="detail-value">
									        {detailData.cemeteryLocationDesc}
									        {detailData.cemeteryLocationLongitude && detailData.cemeteryLocationLatitude ? (
									            <span
									              className="cemetery-nav-icon"
									              title="导航到墓地"
									              onClick={() => {
									                const lat = detailData.cemeteryLocationLatitude;
									                const lng = detailData.cemeteryLocationLongitude;
									                const name = detailData.cemeteryLocationDesc || '墓地';
									                handleLocationNavigation(lat, lng, name, name);
									              }}
									            >
									              📍
									            </span>
									        ) : null}
									    </span>
									</div>):''
									}
										<div key="memberType" className="detail-row">
										    <span className="detail-label">成员类型：</span>
										    <span className="detail-value">{memberTypeText(detailData.memberType)}</span>
										</div>
										<div key="orderIndex" className="detail-row">
										    <span className="detail-label">兄弟排行：</span>
										    <span className="detail-value">{'老'+numberToChineseSimple(detailData.orderIndex)}</span>
										</div>
										<div key="hierarchyCode" className="detail-row">
										    <span className="detail-label">辈分(字号)：</span>
										    <span className="detail-value">{String(detailData.hierarchyCode??'暂无')}</span>
										</div>
										<div key="remark" className="detail-row">
										    <span className="detail-label">备注说明：</span>
										    <span className="detail-value">{String(detailData.remark??'暂无')}</span>
										</div>										
                                    </div>
                                )
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* 地图选择弹窗 */}
            {mapPickerVisible && mapPickerData && (
                <div className="map-picker-overlay" onClick={closeMapPicker}>
                    <div className="map-picker-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="map-picker-header">
                            <span className="map-picker-title">选择导航APP</span>
                            <span className="map-picker-close" onClick={closeMapPicker}>×</span>
                        </div>
                        <div className="map-picker-body">
                            <div className="map-picker-dest">
                                目的地：{mapPickerData.name}
                            </div>
                            <div className="map-picker-grid">
                                {mapApps.map((app) => (
                                    <div
                                        key={app.name}
                                        className="map-picker-item"
                                        onClick={() => handleNavigate(app, mapPickerData)}
                                    >
                                        <span className="map-picker-item-icon">{app.icon}</span>
                                        <span className="map-picker-item-name">{app.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {isWechat && (
                            <div className="map-picker-footer">
                                <span className="map-picker-hint">💡 微信内将打开网页版地图，可在右上角选择"在浏览器打开"</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TreeChart; 